import { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

interface AuthState {
  user: User | null;
  isReady: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthState>({
  user: null,
  isReady: false,
  isAuthenticated: false,
});

/**
 * Initialise auth once at app root. Restores the session from storage
 * (fast, no network call) and then listens for subsequent changes.
 */
export function useAuthProvider(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // getSession restores from local storage — nearly instant
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setIsReady(true);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Record the current sender's IP so the email tracking pipeline can
  // suppress opens that come from the user's own devices (their "Sent"
  // view, prefetching, etc.). Fire once per session, fail silently.
  useEffect(() => {
    if (!user) return;
    const KEY = `selfip:recorded:${user.id}`;
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(KEY)) return;
      sessionStorage.setItem(KEY, "1");
    } catch { /* private mode */ }
    supabase.functions.invoke("record-self-ip", { body: {} }).catch(() => {});
  }, [user]);

  return { user, isReady, isAuthenticated: !!user };
}

export const AuthProvider = AuthContext.Provider;

/** Consume shared auth state from any component without an extra getSession call. */
export function useAuthReady(): AuthState {
  return useContext(AuthContext);
}
